import { getBaseUrl } from "@/lib/utils/get-base-url";
import { getQueryParams } from "@/lib/utils/get-query-params";
import qs from "qs";

const headers = {
  "x-internal-client": "DIGITAL_PROFILE",
  "x-channel": "CONTRACTOR_WEB",
  "Content-Type": "application/json",
};

// The backend returns this placeholder path when a contractor has no profile
// photo. It doesn't exist on the CDN, so treat it as "no picture" and let the
// UI fall back to the local empty-dp image. Matched by exact path (domain
// stripped) so it holds across dev/prod CDN hosts without false positives.
const DEFAULT_PROFILE_PICTURE_PATH = "/uploads/default-profile.png";

const isDefaultProfilePicture = (src) => {
  if (typeof src !== "string") return false;
  const pathOnly = src.replace(/^(https?:\/\/[^/]+)?/, "");
  return pathOnly === DEFAULT_PROFILE_PICTURE_PATH;
};

// Optimized caching for different data types
const profileOptions = {
  method: "GET",
  headers: headers,
  next: {
    revalidate: 600, // 10 minutes for profile data (changes less frequently)
    tags: ["digital-profile"],
  },
};

const galleryOptions = {
  method: "GET",
  headers: headers,
  next: {
    revalidate: 180, // 3 minutes for gallery data (updates more frequently)
    tags: ["project-gallery"],
  },
};

export const getDigitalProfile = async (profileId) => {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BASE_URL}/v1/api/cms/digital-profiles/${profileId}`,
      profileOptions,
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response?.status}`);
    }

    const data = await response.json();
    const digitalProfile = data?.result?.response?.data?.digitalProfile;

    if (digitalProfile && isDefaultProfilePicture(digitalProfile.profilePicture)) {
      digitalProfile.profilePicture = null;
    }

    return digitalProfile;
  } catch (error) {
    console.error("Error while fetching Digital Profile", error);
    throw error;
  }
};

export const fetchProjectMediaGalleries = async ({
  id,
  selectedTag,
  pageNumber = 1,
  sortBy,
}) => {
  try {
    const baseFilters = getQueryParams({
      sortBy,
      digitalProfileId: id,
      selectedTag: selectedTag ?? "All",
      pageNumber: pageNumber,
    });
    const queryString = qs.stringify(baseFilters);
    const response = await fetch(
      `${getBaseUrl()}/v1/api/cms/project-media-galleries?populate=*&${queryString}`,
      galleryOptions,
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const projects = await response.json();
    return projects;
  } catch (error) {
    console.error("Error while fetching Project Media Galleries", error);
    throw error;
  }
};

export const fetchAlbums = async ({ id, page = 1, pageSize = 1000 }) => {
  try {
    const response = await fetch(`${getBaseUrl()}/v1/api/cms/albums/load`, {
      method: "POST",
      headers: headers,
      body: JSON.stringify({
        dpId: id,
        page: page,
        pageSize: pageSize,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const albums = await response.json();
    return albums;
  } catch (error) {
    console.error("Error while fetching Albums", error);
    throw error;
  }
};

export const fetchOrganization = async (orgId) => {
  try {
    const response = await fetch(
      `${getBaseUrl()}/v1/api/organization/${orgId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const organization = data?.result?.data ?? null;

    const addressData = organization?.addresses?.find(
      (item) => item?.projectId == null,
    );
    const part = (value) => {
      if (value == null) return null;
      const s = String(value).trim();
      return s.length ? s : null;
    };
    const fullAddress = [
      part(addressData?.flatNo),
      part(addressData?.addressLine1),
      part(addressData?.addressLine2),
      part(addressData?.city),
      part(addressData?.country),
      part(addressData?.pincode),
    ]
      .filter(Boolean)
      .join(", ");
    const area = [part(addressData?.locality), part(addressData?.city)]
      .filter(Boolean)
      .join(", ");
    return {
      fullAddress,
      area,
      locality: part(addressData?.locality),
      city: part(addressData?.city),
      state: part(addressData?.state),
      pincode: part(addressData?.pincode),
      logo: organization?.logo ?? null,
      businessName:
        organization?.legalEntityName || organization?.orgName || null,
    };
  } catch (error) {
    console.error("Error while fetching Organization", error);
    throw error;
  }
};

export const fetchAlbumDetails = async ({ albumId }) => {
  try {
    const response = await fetch(
      `${getBaseUrl()}/v1/api/cms/albums?populate=*&filters[id][$eq]=${albumId}`,
      {
        method: "GET",
        headers: headers,
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const albumDetails = await response.json();
    return albumDetails?.data?.[0];
  } catch (error) {
    console.error("Error while fetching Album Details", error);
    throw error;
  }
};

export const fetchAlbumMedia = async ({
  albumId,
  digitalProfileId,
  page,
  pageSize,
}) => {
  try {
    const response = await fetch(
      `${getBaseUrl()}/v1/api/cms/albums/fetch-gallery-details`,
      {
        method: "POST",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          albumId: albumId,
          digitalProfileId: +digitalProfileId,
          tagIds: [],
          page: page,
          pageSize: pageSize,
        }),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const albumMedia = await response.json();
    return albumMedia;
  } catch (error) {
    console.error("Error while fetching Album Media", error);
    throw error;
  }
};
